import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function Loading({ label = "Cargando...", testId = "loading-state" }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-slate-500" data-testid={testId}>
      <span className="play-icon h-16 w-16 bg-violet-600"><Loader2 className="h-9 w-9 animate-spin" /></span>
      <p className="font-display text-lg font-bold">{label}</p>
    </div>
  );
}

export function ErrorState({ message, onRetry, testId = "error-state" }) {
  return (
    <div className="play-card flex flex-col items-center gap-4 bg-rose-100 p-8 text-center" data-testid={testId}>
      <span className="play-icon h-16 w-16 bg-rose-500">
        <AlertTriangle className="h-9 w-9" />
      </span>
      <p className="max-w-md text-sm text-slate-600">{message}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} data-testid="error-retry-btn" className="estudia-touch">
          Reintentar
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ title, description, action, testId = "empty-state" }) {
  return (
    <div className="play-card flex flex-col items-center gap-3 bg-cyan-100 p-10 text-center" data-testid={testId}>
      <span className="play-icon h-20 w-20 bg-cyan-500">
        <Inbox className="h-11 w-11" />
      </span>
      <h3 className="font-display text-2xl font-extrabold text-slate-800">{title}</h3>
      {description && <p className="max-w-md text-sm font-semibold text-slate-700">{description}</p>}
      {action}
    </div>
  );
}

const STATUS_STYLES = {
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  processing: "bg-amber-50 text-amber-700 border-amber-200",
  indexing: "bg-violet-50 text-violet-700 border-violet-200",
  queued: "bg-slate-100 text-slate-600 border-slate-200",
  uploaded: "bg-slate-100 text-slate-600 border-slate-200",
  empty: "bg-slate-100 text-slate-500 border-slate-200",
  succeeded: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STATUS_LABELS = {
  ready: "Listo",
  failed: "Con error",
  processing: "Procesando",
  indexing: "Indexando",
  queued: "En cola",
  uploaded: "Subido",
  empty: "Sin material",
  succeeded: "Completado",
};

export function StatusBadge({ status, testId }) {
  const style = STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${style}`}
      data-testid={testId}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function MasteryBar({ value, testId }) {
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right text-xs font-semibold text-slate-600">{pct}%</span>
    </div>
  );
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours) return `${hours} h ${minutes} min`;
  if (minutes) return `${minutes} min`;
  return `${total} s`;
}
